import React, { useState, useEffect } from 'react';
import { X, User, Lock, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import CurrencySelect from './CurrencySelect';

interface AccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({ isOpen, onClose }) => {
  const { user, updateUserSettings } = useAuth();

  const [defaultCurrency, setDefaultCurrency] = useState(user?.default_currency || 'USD');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState(false);
  const [settingsError, setSettingsError] = useState('');

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDefaultCurrency(user?.default_currency || 'USD');
      setSettingsSuccess(false);
      setSettingsError('');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess(false);
      setPasswordError('');
    }
  }, [isOpen, user?.default_currency]);

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    setSettingsSuccess(false);
    setSettingsError('');
    try {
      await updateUserSettings({ default_currency: defaultCurrency });
      setSettingsSuccess(true);
    } catch {
      setSettingsError('Failed to save settings. Please try again.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    setSavingPassword(true);
    try {
      await apiService.changePassword(oldPassword, newPassword);
      setPasswordSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err?.message || 'Failed to change password. Please try again.');
    } finally {
      setSavingPassword(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="expense-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <User size={16} color="var(--text-secondary)" />
            <h2 className="heading-2" style={{ margin: 0 }}>Account Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="btn btn-icon btn-secondary"
            style={{ padding: 'var(--spacing-sm)', width: '32px', height: '32px' }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Profile info */}
          <div style={{ padding: '12px 16px', background: 'var(--background)', borderRadius: 'var(--radius-small)', fontSize: 14 }}>
            <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Email</div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{user?.email}</div>
          </div>

          {/* Default currency */}
          <form onSubmit={handleSaveSettings}>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Default Currency</label>
              <CurrencySelect
                value={defaultCurrency}
                onChange={(val) => { setDefaultCurrency(val); setSettingsSuccess(false); }}
                disabled={savingSettings}
              />
              <div className="form-help">Used as the default display currency across the dashboard.</div>
            </div>

            {settingsError && <div className="text-error" style={{ marginBottom: 8 }}>{settingsError}</div>}
            {settingsSuccess && (
              <div style={{ color: 'var(--success)', fontSize: 13, marginBottom: 8 }}>Settings saved successfully.</div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingSettings || defaultCurrency === user?.default_currency}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Save size={15} />
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </form>

          <div style={{ borderTop: '1px solid var(--border)', margin: '0 -16px', paddingBottom: 4 }} />

          {/* Change password */}
          <form onSubmit={handleChangePassword}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Lock size={15} color="var(--text-secondary)" />
              <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>Change Password</span>
            </div>

            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className="form-input"
                value={oldPassword}
                onChange={(e) => { setOldPassword(e.target.value); setPasswordError(''); }}
                disabled={savingPassword}
                autoComplete="current-password"
              />
            </div>

            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-input"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPasswordError(''); }}
                disabled={savingPassword}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className={`form-input ${passwordError && newPassword !== confirmPassword ? 'error' : ''}`}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(''); }}
                disabled={savingPassword}
                autoComplete="new-password"
              />
            </div>

            {passwordError && <div className="text-error" style={{ marginBottom: 8 }}>{passwordError}</div>}
            {passwordSuccess && (
              <div style={{ color: 'var(--success)', fontSize: 13, marginBottom: 8 }}>Password changed successfully.</div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingPassword || !oldPassword || !newPassword || !confirmPassword}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Lock size={15} />
              {savingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsModal;
